import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { MultiValueStackedBarChart } from "../../../../../../components/charts/MultiValueStackedBarChart/MultiValueStackedBarChart";
import { MultiValueStackedBarChartData } from "../../../../../../components/charts/MultiValueStackedBarChart/types";
import { useGetCompetitorMarketPresence } from "./hooks/useGetCompetitorMarketPresence";
import { useWorkspaceStore } from "../../../../../../store/workspace/WorkspaceStore";
import { ChartExportMenu } from "../../../../../../components/charts/ChartExportMenu";
import * as am4charts from "@amcharts/amcharts4/charts";
import { TAB_VIEW } from "../../../../WorkspaceConfiguration/constants";
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "../../../../../../components/Snackbar";
import { getInsufficientInfoText } from "../../../MarketLandscape/utils";
import { TitleWithTooltipInfo } from "../../../../../../components/charts/TitleWithTooltipInfo";
import {
  CHART_CONSTANTS,
  INSUFFICIENT_COMPETITORS_INFO_TEXT,
} from "../../../constants";

const { tooltip, baseId, chartTitle, legend, csvFieldMappings } =
  CHART_CONSTANTS.competitorMarketPresence;
const LEGEND_TITLE_ITEMS = [
  {
    name: legend,
    color: "#0288d1",
  },
];

export const CompetitorMarketPresence = () => {
  const [isChartHovered, setIsChartHovered] = useState(false);
  const { tam, sam, som, isLoading } = useGetCompetitorMarketPresence();
  const navigate = useNavigate();
  const { id: workspaceId } = useParams();
  const { showInfo } = useSnackbar();
  const isSamConfigured = !useWorkspaceStore((state) =>
    state.getIsSamDisabled()
  );
  const isSomConfigured = !useWorkspaceStore((state) =>
    state.getIsSomDisabled()
  );
  const chartRef = useRef<am4charts.XYChart>(null);

  const shouldShowExport = chartRef && !isLoading;

  const areCompetitiveProductsConfigured = useWorkspaceStore(
    (state) =>
      (state.draft?.configuration.competitorsProductIds || []).length > 0
  );

  const chartData = useMemo<MultiValueStackedBarChartData[]>(() => {
    return [
      {
        segmentName: "TAM",
        current: tam.currentCustomers,
        totalSpend: tam.totalSpend,
        totalCompanies: tam.companyCount,
        // TAM 'should' always be configured
        isSegmentConfigured: true,
        isAdditionalInfoConfigured: areCompetitiveProductsConfigured,
      },
      {
        segmentName: "SAM",
        current: isSamConfigured ? sam.currentCustomers : 0,
        totalSpend: isSamConfigured ? sam.totalSpend : 0,
        totalCompanies: isSamConfigured ? sam.companyCount : 0,
        isSegmentConfigured: isSamConfigured,
        isAdditionalInfoConfigured: areCompetitiveProductsConfigured,
      },
      {
        segmentName: "SOM",
        current: isSomConfigured ? som.currentCustomers : 0,
        totalSpend: isSomConfigured ? som.totalSpend : 0,
        totalCompanies: isSomConfigured ? som.companyCount : 0,
        isSegmentConfigured: isSomConfigured,
        isAdditionalInfoConfigured: areCompetitiveProductsConfigured,
      },
    ];
  }, [
    sam.companyCount,
    sam.currentCustomers,
    sam.totalSpend,
    som.companyCount,
    som.currentCustomers,
    som.totalSpend,
    tam.companyCount,
    tam.currentCustomers,
    tam.totalSpend,
    isSamConfigured,
    isSomConfigured,
    areCompetitiveProductsConfigured,
  ]);

  const handleInsufficientButtonClick = useCallback(
    (segment: TAB_VIEW) => {
      navigate(`/projects/${workspaceId}/configuration`);
      showInfo(getInsufficientInfoText(segment));
    },
    [navigate, showInfo, workspaceId]
  );

  const handleCompetitorsConfigClick = useCallback(() => {
    navigate(`/projects/${workspaceId}/configuration/competitors`);
    showInfo(INSUFFICIENT_COMPETITORS_INFO_TEXT);
  }, [navigate, showInfo, workspaceId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        bgcolor: "#fff",
        height: "400px",
        borderRadius: "10px",
        border: "solid 1px #edeff1",
        padding: "20px 19px 10px 20px",
      }}
      onMouseEnter={() => setIsChartHovered(true)}
      onMouseLeave={() => setIsChartHovered(false)}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        paddingBottom="10px"
      >
        <TitleWithTooltipInfo
          chartTitle={chartTitle}
          tooltipTitle={tooltip}
          showInfoIcon={isChartHovered}
        />

        {shouldShowExport && (
          <ChartExportMenu
            chart={chartRef}
            title={chartTitle}
            csvFieldMappings={csvFieldMappings}
          />
        )}
      </Box>
      {isLoading ? (
        <Skeleton variant="rounded" width="100%" height="300px" />
      ) : (
        <Box position="relative" width="100%" height="100%">
          <MultiValueStackedBarChart
            id={`${baseId}-chart`}
            chartData={chartData}
            onInsufficientButtonClick={handleInsufficientButtonClick}
            onAdditionalInfoLinkClick={handleCompetitorsConfigClick}
            ref={chartRef}
            legendItems={LEGEND_TITLE_ITEMS}
            showOnlyCurrentCompanies={true}
          />
        </Box>
      )}
    </Box>
  );
};
